import { useState, useEffect, useContext, useCallback } from "react";
import { Icon } from '@iconify/react/dist/iconify.js';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ProvisionalReportDistrictSummary from './ProvisionalReportDistrictSummary';
import ProvincialReportDepartmentSummary from './ProvincialReportDepartmentSummary';
import { LoginContext } from "../AuthContext/LoginContext";

const ProvisionalActivityPage = () => {
  const [departmentData, setDepartmentData] = useState([]);
  const [districtData, setDistrictData] = useState([]);
  const [loading, setLoading] = useState(true);
  const API_BASE_URL = process.env.REACT_APP_BE_API_URL;
  const { user } = useContext(LoginContext);

  // Toast helper (memoized)
  const showToast = useCallback((message, type = "success") => {
    const toastOptions = {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    };
    switch (type) {
      case "success":
        toast.success(message, toastOptions);
        break;
      case "error":
        toast.error(message, toastOptions);
        break;
      default:
        toast(message, toastOptions);
    }
  }, []);

  // Fetch data (now depends on showToast)
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      // Fetch department summary
      const deptResponse = await axios.post(
        `${API_BASE_URL}/api/plantation/pafs/department-summary`,
        { province: user.province },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (deptResponse.data.status) {
        setDepartmentData(deptResponse.data.data);
      }

      // Fetch district summary
      const districtResponse = await axios.post(
        `${API_BASE_URL}/api/plantation/pafs/only-public-district-summary`,
        { province: user.province },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (districtResponse.data.status) {
        setDistrictData(districtResponse.data.data);
      }

    } catch (error) {
      console.error("Error fetching data:", error);
      showToast("Failed to fetch data. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  }, [user.province, API_BASE_URL, showToast]); // ← Added showToast

  // Initial load
  useEffect(() => {
    if (user.province) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [fetchData, user.province]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="col-lg-12 col-md-12 col-sm-12">
      <ToastContainer />
      <div className="text-center">
        <h6 className="mb-20 text-2xl font-bold text-gray-800 flex items-center justify-center">
          <Icon icon="mdi:file-chart-outline" className="text-3xl mx-3 my-1 text-success-900" />
          <span className="text-success-700 animation-text-1">{user.province}</span> Provisional Activity Report
        </h6>
      </div>

      <div className="col-xxl-12">
        <div className="card p-0 overflow-hidden position-relative radius-12 h-100">
          <div className="card-body bg-gradient-success p-24 pt-10">
            <ul className="nav button-tab nav-pills mb-16" id="pills-tab-four" role="tablist">
              <li className="nav-item" role="presentation">
                <button
                  className="nav-link d-flex align-items-center gap-2 fw-semibold text-primary-light radius-4 px-16 py-10 active"
                  id="pills-button-icon-home-tab"
                  data-bs-toggle="pill"
                  data-bs-target="#pills-button-icon-home"
                  type="button"
                  role="tab"
                  aria-controls="pills-button-icon-home"
                  aria-selected="true"
                >
                  <Icon icon="solar:home-smile-angle-outline" className="text-xl" />
                  <span className="line-height-1">{user.province} General Public</span>
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className="nav-link d-flex align-items-center gap-2 fw-semibold text-primary-light radius-4 px-16 py-10"
                  id="pills-button-icon-details-tab"
                  data-bs-toggle="pill"
                  data-bs-target="#pills-button-icon-details"
                  type="button"
                  role="tab"
                  aria-controls="pills-button-icon-details"
                  aria-selected="false"
                >
                  <Icon icon="hugeicons:folder-details" className="text-xl" />
                  <span className="line-height-1">{user.province} Departmental</span>
                </button>
              </li>
            </ul>

            <div className="tab-content" id="pills-tab-fourContent">
              {/* General Public Tab */}
              <div
                className="tab-pane fade show active"
                id="pills-button-icon-home"
                role="tabpanel"
                aria-labelledby="pills-button-icon-home-tab"
                tabIndex={0}
              >
                <ProvisionalReportDistrictSummary districtData={districtData} province={user.province} />
              </div>

              {/* Departmental Tab */}
              <div
                className="tab-pane fade"
                id="pills-button-icon-details"
                role="tabpanel"
                aria-labelledby="pills-button-icon-details-tab"
                tabIndex={0}
              >
                <ProvincialReportDepartmentSummary departmentData={departmentData} province={user.province} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProvisionalActivityPage;